import React, { useEffect, useState } from 'react';
import { apiClient } from '../services/api';
import { ymGoal } from '../utils/ym';
import { COUNTRIES, paymentMethodForCountry } from '../utils/countries';
import type { SubscriptionPlan, TrialStatus } from '../types';

/**
 * Раздел подписок на странице «Баланс»: тарифы (350/600/1000) + попап пробного
 * периода после выбора плана (логика-аналог luniva). Оплата подключится позже.
 */
export const SubscriptionSection: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [trial, setTrial] = useState<TrialStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<SubscriptionPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  // Выбор страны перед оплатой (Sofa 02.09): РФ → карта, остальные → крипта.
  const [country, setCountry] = useState<string>('RU');
  const paymentMethod = paymentMethodForCountry(country);

  const loadTrial = async () => {
    try { setTrial(await apiClient.getTrialStatus()); } catch { /* noop */ }
  };

  useEffect(() => {
    Promise.all([
      apiClient.getSubscriptionPlans().then(setPlans).catch(() => setPlans([])),
      loadTrial(),
    ]).finally(() => setLoading(false));
  }, []);

  const planLabel = (days: number): string => {
    if (days >= 30) return 'месяц';
    if (days >= 14) return '2 недели';
    if (days >= 7) return 'неделя';
    return `${days} дн.`;
  };

  const formatDate = (iso: string | null): string => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }); }
    catch { return iso; }
  };

  const openPlan = (p: SubscriptionPlan) => { setMessage(null); setConsent(false); setSelected(p); };

  // Оформление подписки: создаём рекуррент-подписку через vspay (триал 3 дня) и
  // открываем страницу оплаты (СБП-привязка).
  const paySubscription = async () => {
    if (!selected || busy) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await apiClient.createSubscriptionPayment(selected.id, { country, payment_method: paymentMethod });
      if (res.paid) {
        // Цель Яндекс.Метрики: подписка оформлена (бесплатный план — сразу) (Соня 29.07).
        ymGoal('subscription-purchase');
        setSelected(null); await loadTrial(); return;
      }
      if (res.success && res.payment_url) {
        // Цель Яндекс.Метрики: инициирована оплата подписки (Соня 29.07).
        ymGoal('subscription-purchase');
        const tg = (window as any).Telegram?.WebApp;
        if (tg?.openLink) tg.openLink(res.payment_url);
        else window.open(res.payment_url, '_blank');
      } else if (res.crypto_pending) {
        // Крипто-провайдер ещё подключается (Егор) — сообщаем в модалке.
        setMessage(res.message ?? 'Крипто-оплата скоро будет доступна.');
      } else {
        setMessage(res.message ?? 'Не удалось создать платёж.');
      }
    } catch (e: any) {
      setMessage(e?.response?.data?.message ?? 'Не удалось создать платёж.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  const trialDays = trial?.duration_days ?? 3;

  // ===== стили =====
  const wrap: React.CSSProperties = { margin: '8px 0 16px' };
  const card = (active: boolean): React.CSSProperties => ({
    flex: '1 1 0', minWidth: 92, padding: '12px 8px', borderRadius: 14, cursor: 'pointer', textAlign: 'center',
    border: active ? '2px solid #7c6cff' : '1px solid rgba(0,0,0,0.08)',
    background: active ? 'linear-gradient(135deg,rgba(124,108,255,0.12),rgba(232,184,150,0.10))' : '#fff',
  });
  const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 };
  const modal: React.CSSProperties = { width: '100%', maxWidth: 380, background: '#fff', color: '#1a1a1a', borderRadius: 18, padding: 18 };
  const btnPrimary: React.CSSProperties = { width: '100%', padding: 12, borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#7c6cff,#9d7bff)', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: busy || !consent ? 0.6 : 1 };
  const btnGhost: React.CSSProperties = { width: '100%', padding: 10, borderRadius: 12, border: '1px solid rgba(0,0,0,0.15)', background: 'transparent', color: '#555', fontSize: 14, cursor: 'pointer', marginTop: 8 };

  return (
    <div style={wrap}>
      <p style={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a', margin: '0 0 4px' }}>Подписка</p>

      {trial?.on_trial ? (
        <div style={{ ...card(true), textAlign: 'left', cursor: 'default' }}>
          <p style={{ margin: 0, fontWeight: 700, color: '#1a1a1a' }}>Пробный период активен</p>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#555' }}>
            Полный доступ до {formatDate(trial.trial_ends_at)}. По окончании можно оформить подписку.
          </p>
        </div>
      ) : (
        <>
          <div style={{ margin: '0 0 12px', padding: '12px 14px', background: '#f6f4ff', borderRadius: 12 }}>
            <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>Что входит в подписку</p>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#555', lineHeight: 1.6 }}>
              <li>доступ ко всем платным медитациям;</li>
              <li>новые медитации открываются автоматически;</li>
              <li>доступ действует в течение выбранного периода.</li>
            </ul>
          </div>
          <p style={{ fontSize: 13, color: '#666', margin: '0 0 10px' }}>Выбери тариф — и начни с бесплатного пробного периода.</p>
          <div style={{ display: 'flex', gap: 8 }}>
            {plans.map((p) => (
              <div key={p.id} style={card(false)} onClick={() => openPlan(p)}>
                <p style={{ margin: 0, fontSize: 13, color: '#666' }}>{planLabel(p.duration_days)}</p>
                <p style={{ margin: '4px 0 0', fontWeight: 800, fontSize: 18, color: '#1a1a1a' }}>{p.price} ₽</p>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: '#7c6cff', fontWeight: 600 }}>Выбрать план</p>
              </div>
            ))}
          </div>
        </>
      )}

      {selected && (
        <div style={overlay} onClick={() => !busy && setSelected(null)}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <p style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#7c6cff', margin: 0, fontWeight: 700 }}>Подписка</p>
            <p style={{ fontSize: 13, color: '#666', margin: '2px 0 12px' }}>Тариф «{selected.name}» — {selected.price} ₽ / {planLabel(selected.duration_days)}</p>

            <p style={{ fontSize: 18, fontWeight: 800, color: '#1a1a1a', margin: '0 0 6px' }}>
              {trialDays} {trialDays === 1 ? 'день' : trialDays < 5 ? 'дня' : 'дней'} бесплатно
            </p>
            <p style={{ fontSize: 14, color: '#555', margin: '0 0 14px' }}>
              Первые {trialDays} дн. — бесплатно, далее {selected.price} ₽ за {planLabel(selected.duration_days)} с автопродлением. Отменить можно в любой момент.
            </p>
            {/* Выбор страны + способ оплаты (Sofa 02.09): РФ → карта, остальные → крипта. */}
            <div style={{ marginBottom: 14 }}>
              <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>Ваша страна</p>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)', fontSize: 14, color: '#1a1a1a', background: '#fff' }}
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                ))}
              </select>
              <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(124,108,255,0.35)', background: 'rgba(124,108,255,0.06)', fontSize: 13, color: '#1a1a1a' }}>
                {paymentMethod === 'card' ? (
                  <><b>Банковская карта</b> <span style={{ color: '#666' }}>· Visa, Mastercard, МИР</span></>
                ) : (
                  <><b>Оплата криптовалютой</b> <span style={{ color: '#666' }}>· USDT, BTC, ETH</span></>
                )}
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#888' }}>Способ оплаты зависит от страны. Изменить можно в профиле.</p>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: '#444', marginBottom: 14 }}>
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>Я согласен на оформление подписки с пробным периодом и условия сервиса.</span>
            </label>
            <button style={btnPrimary} onClick={paySubscription} disabled={busy || !consent}>
              {busy ? 'Оформляем…' : 'Оформить подписку'}
            </button>

            {message && <p style={{ fontSize: 13, color: '#c0392b', marginTop: 10 }}>{message}</p>}
            <button style={btnGhost} onClick={() => setSelected(null)} disabled={busy}>Закрыть</button>
          </div>
        </div>
      )}
    </div>
  );
};
